import {useEffect, useRef, Fragment, useCallback, useState} from "react";
import {EditorContent, EditorContext, useEditor} from "@tiptap/react";
import {useTranslation} from "react-i18next";
import {inject, observer} from "mobx-react";
import debounce from "lodash/debounce";
import cn from "classnames";

//local
import {LockValue} from "components/LockValue";
import {hasPermission} from "helpers/helper";
import {EditorTemplatesModal} from "components/modals/EditorTemplatesModal";

// --- Tiptap Core Extensions ---
import {StarterKit} from "@tiptap/starter-kit";
import {TaskItem, TaskList} from "@tiptap/extension-list";
import {TextAlign} from "@tiptap/extension-text-align";
import {Typography} from "@tiptap/extension-typography";
import {Highlight} from "@tiptap/extension-highlight";
import {Subscript} from "@tiptap/extension-subscript";
import {Superscript} from "@tiptap/extension-superscript";
import {Selection} from "@tiptap/extensions";
import Placeholder from "@tiptap/extension-placeholder";
import {FontSize, TextStyle} from "@tiptap/extension-text-style";

// --- UI Primitives ---
// import {Button} from "components/RichEditor/components/tiptap-ui-primitive/button";
import {Spacer} from "components/RichEditor/components/tiptap-ui-primitive/spacer";
import {
  Toolbar,
  ToolbarGroup,
  // ToolbarSeparator,
} from "components/RichEditor/components/tiptap-ui-primitive/toolbar";

// --- Tiptap Node ---
import {HorizontalRule} from "components/RichEditor/components/tiptap-node/horizontal-rule-node/horizontal-rule-node-extension";
import {FormField} from "components/RichEditor/components/tiptap-node/form-field-node/form-field-node-extension";
import {Checkbox} from "components/RichEditor/components/tiptap-node/checkbox-node/checkbox-node-extension";
import "components/RichEditor/components/tiptap-node/blockquote-node/blockquote-node.scss";
import "components/RichEditor/components/tiptap-node/horizontal-rule-node/horizontal-rule-node.scss";
import "components/RichEditor/components/tiptap-node/list-node/list-node.scss";
import "components/RichEditor/components/tiptap-node/heading-node/heading-node.scss";
import "components/RichEditor/components/tiptap-node/paragraph-node/paragraph-node.scss";
import "components/RichEditor/components/tiptap-node/text-align-node/text-align-node.scss";
import "components/RichEditor/components/tiptap-node/form-field-node/form-field-node.scss";
import "components/RichEditor/components/tiptap-node/checkbox-node/checkbox-node.scss";

// --- Tiptap UI ---
import {HeadingDropdownMenu} from "components/RichEditor/components/tiptap-ui/heading-dropdown-menu";
import {BlockquoteButton} from "components/RichEditor/components/tiptap-ui/blockquote-button";
import {MarkButton} from "components/RichEditor/components/tiptap-ui/mark-button";
import {UndoRedoButton} from "components/RichEditor/components/tiptap-ui/undo-redo-button";
import {ListButton} from "components/RichEditor/components/tiptap-ui/list-button";
import {TextAlignPopover} from "components/RichEditor/components/tiptap-ui/text-align-popover";
import {FontSizeControl} from "components/RichEditor/components/tiptap-ui/font-size-control";
import {FormFieldButton} from "components/RichEditor/components/tiptap-ui/form-field-button";
import {CheckboxButton} from "components/RichEditor/components/tiptap-ui/checkbox-button";
import {AddTemplateButton} from "components/RichEditor/components/tiptap-ui/add-template-button";
import {SelectTemplateButton} from "components/RichEditor/components/tiptap-ui/select-template-button";
import HandyPointerBlob from "components/HandyPointerBlob";

// --- Styles ---
import "./rich-editor.scss";

// --- Utils ---
import {templatesTypePermission} from "./index";

export const RichEditor = inject("store")(
  observer(
    ({
      store: {auth},
      initialValue,
      placeholder,
      mode = "edit",
      exportType = "json",
      height,
      toolbar,
      needInputDebounce = true,
      showTemplateButtons = false,
      showExtendedToolbarButtons = false,
      templatesType,
      onChange,
      className,
    }) => {
      const {isStaff} = auth;
      const changeRef = useRef(null);
      const {t} = useTranslation();

      const [editorTemplatesModalOpenType, setEditorTemplatesModalOpenType] =
        useState(null);

      const isEdit = mode === "edit";
      const isReadonly = mode === "readonly";
      const isView = mode === "view";
      const showToolbar = isEdit || isReadonly;

      const editor = useEditor({
        immediatelyRender: false,
        editorProps: {
          attributes: {
            autocomplete: "off",
            autocorrect: "off",
            autocapitalize: "off",
            "aria-label": "Main content area, start typing to enter text.",
            class: "rt-tiptap-editor",
          },
        },
        extensions: [
          StarterKit.configure({horizontalRule: false}),
          HorizontalRule,
          TextAlign.configure({types: ["heading", "paragraph"]}),
          TaskList,
          TaskItem.configure({nested: true}),
          Highlight.configure({multicolor: true}),
          Typography,
          Superscript,
          Subscript,
          Selection,
          TextStyle,
          FontSize,
          Placeholder.configure({
            placeholder: ({editor}) =>
              editor.isEmpty
                ? mode === "view"
                  ? t("NO_TEXT")
                  : t(placeholder || "NO_PLACEHOLDER")
                : "",
            showOnlyWhenEditable: isReadonly,
          }),
          FormField.configure({placeholder: "Enter field label"}),
          Checkbox,
        ],
        content: initialValue ?? "",
        onUpdate({editor, transaction}) {
          if (!transaction.docChanged || !isEdit || !onChange) return;
          changeRef.current?.(editor);
        },
        editable: isEdit,
      });

      const handleAddTemplate = () => {
        if (!hasPermission([templatesTypePermission[templatesType]])) return;
        setEditorTemplatesModalOpenType("add");
      };
      const handleSelectTemplate = () => {
        if (!hasPermission([templatesTypePermission[templatesType]])) return;
        setEditorTemplatesModalOpenType("select");
      };
      const clickSelectTemplate = (template) =>
        editor.commands.setContent(template.content ?? "");

      const ToolbarContent = () => {
        return (
          <Fragment>
            <ToolbarGroup>
              <UndoRedoButton action="undo" />
              <UndoRedoButton action="redo" />
            </ToolbarGroup>
            <ToolbarGroup>
              <MarkButton type="bold" />
              <MarkButton type="italic" />
              <MarkButton type="underline" />
              <MarkButton type="strike" />
              <MarkButton type="superscript" />
              <MarkButton type="subscript" />
              <BlockquoteButton />
            </ToolbarGroup>
            <ToolbarGroup>
              <HeadingDropdownMenu levels={[1, 2, 3, 4, 5, 6]} />
            </ToolbarGroup>
            <ToolbarGroup>
              <FontSizeControl />
            </ToolbarGroup>
            <ToolbarGroup>
              <ListButton type="bulletList" />
              <ListButton type="orderedList" />
            </ToolbarGroup>
            <ToolbarGroup>
              <TextAlignPopover aligns={["left", "center", "right", "justify"]} />
            </ToolbarGroup>
            {showExtendedToolbarButtons && (
              <ToolbarGroup>
                <CheckboxButton />
                <FormFieldButton />
              </ToolbarGroup>
            )}

            <Spacer />
            {showTemplateButtons && (
              <LockValue
                canPermissions={[templatesTypePermission[templatesType]]}
                needLabel
                needShowModalToSubscribe
              >
                <ToolbarGroup>
                  <HandyPointerBlob
                    showPointer
                    pointerAnimation
                    pointerPosition="last"
                    pointerDirection="right"
                    hideOnScreenWidthUnder={1240}
                    fadeOutAfter={20000}
                    text="RICH_EDITOR_TEMPLATE_POINTER_TEXT"
                    addClass="mr-2"
                  />
                  {isStaff && <AddTemplateButton onClick={handleAddTemplate} />}
                  <SelectTemplateButton onClick={handleSelectTemplate} />
                </ToolbarGroup>
              </LockValue>
            )}
          </Fragment>
        );
      };

      const renderToolbar = () => {
        if (toolbar === false || !showToolbar) return null;
        if (!editor) return null;
        if (typeof toolbar === "function") return toolbar(editor);
        if (toolbar) return toolbar;

        return <ToolbarContent />;
      };

      const toolbarContent = renderToolbar();

      const handleChangeRef = useCallback(
        (editor) => {
          const value = exportType === "json" ? editor.getJSON() : editor.getHTML();

          onChange?.(value);
        },
        [exportType, onChange]
      );

      useEffect(() => {
        if (!editor) return;
        editor.setEditable(isEdit);
      }, [editor, isEdit]);

      useEffect(() => {
        changeRef.current = needInputDebounce
          ? debounce(handleChangeRef, 1000)
          : handleChangeRef;

        return () => {
          needInputDebounce && changeRef.current?.cancel();
        };
      }, [onChange, exportType, handleChangeRef, needInputDebounce]);

      if (!editor) return null;

      return (
        <div
          className={cn("rt-tiptap-editor-wrapper", {
            view_mode: isView,
            readonly_mode: mode === "readonly",
            [className]: !!className,
          })}
        >
          <EditorContext.Provider value={{editor}}>
            {toolbarContent && <Toolbar>{toolbarContent}</Toolbar>}

            <EditorContent
              editor={editor}
              role="presentation"
              className="rt-tiptap-editor-content"
              style={{
                minHeight: `${height?.[0] ?? 150}px`,
                maxHeight: height?.[1] ? `${height[1]}px` : undefined,
                overflowY: isView ? "visible" : "auto",
              }}
            />
          </EditorContext.Provider>
          {!!editorTemplatesModalOpenType && (
            <EditorTemplatesModal
              isOwner={isStaff}
              outsideData={{
                openType: editorTemplatesModalOpenType,
                type: templatesType,
                content: exportType === "json" ? editor.getJSON() : editor.getHTML(),
              }}
              onChangeTemplate={clickSelectTemplate}
              onClose={() => setEditorTemplatesModalOpenType(null)}
            />
          )}
        </div>
      );
    }
  )
);
